import { DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import type { User } from '../../@types/User';
import { setCompanyToolsTimeline } from '../../store/AuthReducer';
import type { RootState } from '../../store/store';
import { completeCompanyToolsTimelineEvent, getMyCompanyRoles, getSalaries } from '../../utils/api';
import { currentPlanningCycleYear, resolveBoitageRowVisibility } from '../../utils/boitageTimeline';
import { JWTAuthorization, JWTHeader } from '../../utils/JWTHeader';
import { ROUTES } from '../../utils/routes';
import { extractUniqueUsersFromFonctionResults } from '../../utils/users';
import { showToast } from '../common/Notification';
import CRMSummaryModule from './components/CRMSummaryModule';
import DashboardStrategySection from './components/DashboardStrategySection';
import DashboardWelcomeCard from './components/DashboardWelcomeCard';
import Header from './components/Header';
import ModulesOverview from './components/ModulesOverview';
import { createUserSections, getReferentPlanningRowKeys } from './components/ModulesOverview/data';
import NextActionsSection from './components/NextActionsSection';
import PlanningModule from './components/PlanningModule';
import RolesDemoPopup from './components/RolesDemoPopup';
import SubscriptionBanner from './components/SubscriptionBanner';
import type { TeamAction } from './utils/nextActions';
import { computeNextActions, computeTeamActions } from './utils/nextActions';
import { getRoleSectionLabel, resolveCapabilities } from './utils/roleResolver';

function StrategyModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) { return null; }
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) { onClose(); } }}
    >
      <div className="relative max-h-[80vh] w-[90%] max-w-[680px] overflow-y-auto rounded-[14px] bg-white p-8 shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3.5 right-4 text-gray-400 transition-colors hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <DashboardStrategySection />
      </div>
    </div>
  );
}

function NewDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAdmin = useSelector((state: RootState) => state.auth.isAdmin);
  const { company, jwt, currentUserId, currentUser } = useSelector((state: RootState) => ({
    company: state.auth.company,
    jwt: state.auth.token,
    currentUserId: state.auth.user?.id ?? null,
    currentUser: state.auth.user ?? null,
  }));
  const [companyRoles, setCompanyRoles] = useState<string[] | null>(null);
  const [isRolesLoading, setIsRolesLoading] = useState<boolean>(true);
  const [isRolesPreviewEnabled, setIsRolesPreviewEnabled] = useState<boolean>(false);
  const [demoRoles, setDemoRoles] = useState<string[]>([]);
  const [isRolesPopupOpen, setIsRolesPopupOpen] = useState<boolean>(false);
  const [companyUsers, setCompanyUsers] = useState<User[]>([]);

  useEffect(() => {
    const fetchRoles = async() => {
      setIsRolesLoading(true);
      if (!company?.id || !jwt) {
        setCompanyRoles(null);
        setIsRolesLoading(false);
        return;
      }
      try {
        const response = await fetch(getMyCompanyRoles(company.id), {
          headers: {
            Authorization: JWTAuthorization(jwt),
          },
        });
        if (response.ok) {
          const data = await response.json();
          setCompanyRoles(Array.isArray(data.roles) ? data.roles : []);
        } else {
          setCompanyRoles([]);
        }
      } catch {
        setCompanyRoles([]);
      } finally {
        setIsRolesLoading(false);
      }
    };
    fetchRoles();
  }, [company?.id, jwt]);

  useEffect(() => {
    if (company?.id && jwt) {
      fetch(getSalaries(company.id, true), JWTHeader(jwt))
        .then((response) => response.json())
        .then((data) => {
          if (data?.results) {
            setCompanyUsers(
              extractUniqueUsersFromFonctionResults(
                data.results,
                (fonctionResult) => fonctionResult.company?.id === company.id,
              ),
            );
          }
        })
        .catch(() => {});
    }
  }, [company?.id, jwt]);

  useEffect(() => {
    if (!isRolesPreviewEnabled) {
      setDemoRoles([]);
    }
  }, [isRolesPreviewEnabled]);

  const effectiveRoles = useMemo(() => {
    if (isRolesPreviewEnabled) {
      return demoRoles;
    }
    return companyRoles ?? [];
  }, [companyRoles, demoRoles, isRolesPreviewEnabled]);

  const effectiveIsAdmin = useMemo(() => (isRolesPreviewEnabled ? false : isAdmin), [isAdmin, isRolesPreviewEnabled]);

  const referentPlanningRowKeys = useMemo(
    () => getReferentPlanningRowKeys(createUserSections(() => {})),
    [],
  );

  const userAssignedRowKeys = useMemo(() => {
    if (!currentUserId || !company?.toolsTimeline) { return []; }
    const cycleYear = currentPlanningCycleYear(company.toolsTimeline);
    const savedAssignments = company.toolsTimeline[String(cycleYear)]?.personAssignments;
    if (!savedAssignments) { return []; }
    return referentPlanningRowKeys.filter((key) => savedAssignments[key] === currentUserId);
  }, [currentUserId, company?.toolsTimeline, referentPlanningRowKeys]);

  const isResponsibleForReferentModule = userAssignedRowKeys.length > 0;

  const capabilities = useMemo(
    () => resolveCapabilities(effectiveRoles, effectiveIsAdmin, isResponsibleForReferentModule),
    [effectiveRoles, effectiveIsAdmin, isResponsibleForReferentModule],
  );

  const restrictedToRowKeys = useMemo(() => {
    if (capabilities.canEditPlanning) { return undefined; }
    if (!isResponsibleForReferentModule) { return undefined; }
    return userAssignedRowKeys;
  }, [capabilities.canEditPlanning, isResponsibleForReferentModule, userAssignedRowKeys]);

  const boitageEnabled = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return resolveBoitageRowVisibility(company, currentYear);
  }, [company]);

  const cycleYear = useMemo(() => currentPlanningCycleYear(company?.toolsTimeline), [company?.toolsTimeline]);

  const timelineData = company?.toolsTimeline?.[String(cycleYear)] ?? null;

  const nextActions = useMemo(() => {
    if (!capabilities.showNextActions) { return []; }
    const savedAssignments = timelineData?.personAssignments;
    return computeNextActions(
      timelineData,
      savedAssignments ?? undefined,
      currentUserId,
      3,
      undefined,
      effectiveIsAdmin ? undefined : effectiveRoles,
      cycleYear,
    );
  }, [capabilities.showNextActions, timelineData, currentUserId, effectiveIsAdmin, effectiveRoles, cycleYear]);

  const teamActions = useMemo(() => {
    if (!capabilities.canEditPlanning) { return []; }
    const savedAssignments = timelineData?.personAssignments;
    return computeTeamActions(
      timelineData,
      savedAssignments ?? undefined,
      currentUserId,
      undefined,
      cycleYear,
    );
  }, [capabilities.canEditPlanning, timelineData, currentUserId, cycleYear]);

  const handleMarkTeamActionDone = useCallback(async(action: TeamAction) => {
    if (!company?.id || !jwt || !action.eventKey) { return; }
    try {
      const response = await fetch(completeCompanyToolsTimelineEvent(company.id), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: JWTAuthorization(jwt),
        },
        // Stored on the cycle the dashboard reads, so the occurrence comes back
        // with next year's planning.
        body: JSON.stringify({ year: cycleYear, eventKey: action.eventKey }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        showToast('error', errorData?.message || 'Erreur lors de la mise à jour de l\'action.');
        return;
      }
      const updatedCompany = await response.json();
      if (updatedCompany?.toolsTimeline) {
        dispatch(setCompanyToolsTimeline(updatedCompany.toolsTimeline));
      }
      showToast('success', 'Action marquée comme faite pour ce cycle.');
    } catch {
      showToast('error', 'Erreur lors de la mise à jour de l\'action.');
    }
  }, [company?.id, jwt, cycleYear, dispatch]);

  const allSections = useMemo(() => createUserSections(navigate), [navigate]);

  const userName = currentUser
    ? `${currentUser.firstname ?? ''} ${currentUser.lastname ?? ''}`.trim()
    : undefined;

  const [isPlanningOpen, setIsPlanningOpen] = useState(true);
  const [isStrategyModalOpen, setIsStrategyModalOpen] = useState(false);

  const isFullLayout = capabilities.layoutMode === 'full';

  const focusedSectionLabel = useMemo(
    () => getRoleSectionLabel(capabilities.primaryRole),
    [capabilities.primaryRole],
  );

  const focusedModuleCount = useMemo(() => {
    if (isFullLayout || !capabilities.visibleSectionIds) { return 0; }
    return allSections
      .filter((s) => capabilities.visibleSectionIds!.includes(s.id))
      .reduce((sum, s) => sum + s.modules.length, 0);
  }, [isFullLayout, capabilities.visibleSectionIds, allSections]);

  return (
    <div className="relative min-h-screen leading-relaxed text-gray-800">
      <div className="container mx-auto px-8 py-8">
        <Header
          isAdmin={isAdmin}
          isRolesPreviewEnabled={isRolesPreviewEnabled}
          onToggleRolesPreview={() => setIsRolesPreviewEnabled((v) => !v)}
          onOpenRolesPopup={() => setIsRolesPopupOpen(true)}
        />

        <div className="mt-6 flex flex-col gap-6">
          {/* Welcome card — identical structure for both layouts */}
          <DashboardWelcomeCard
            canSeeAnalysis={capabilities.canSeeAnalysis}
            showStrategyButton={isFullLayout}
            onOpenStrategyModal={() => setIsStrategyModalOpen(true)}
          />

          {isAdmin && (
            <div className="flex justify-center">
              <Link
                to={ROUTES.QR_CODE_ASSISTANT}
                className="inline-flex items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-6 py-3 text-sm font-semibold text-rgpd-blue shadow-sm transition-all duration-200 hover:bg-gray-50 hover:shadow-md"
              >
                <DevicePhoneMobileIcon className="h-5 w-5" />
                Accéder au module assistant immobilier
              </Link>
            </div>
          )}

          {isFullLayout ? (
            <>
              {/* ═══ Full Layout (admin / dirigeant / referent) ═══ */}
              {capabilities.canSeeCRM && <CRMSummaryModule />}

              {/* Section 1: Next actions + team tracking */}
              {(nextActions.length > 0 || teamActions.length > 0) && (
                <NextActionsSection
                  actions={nextActions}
                  userName={userName}
                  companyUsers={companyUsers}
                  teamActions={capabilities.canEditPlanning ? teamActions : undefined}
                  onMarkTeamActionDone={handleMarkTeamActionDone}
                />
              )}

              {/* Section 2: All modules by service (collapsed) */}
              <div className="flex items-center justify-between rounded-[10px] bg-gradient-to-r from-rgpd-blue-dark to-rgpd-blue px-5 py-3.5 shadow-md">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-base font-extrabold text-white">
                      Les outils du module CROISSANCE par service
                    </div>
                    <div className="mt-0.5 text-xs font-normal text-white/75">
                      Chaque service dispose de modules spécialement adaptés à ses besoins
                    </div>
                  </div>
                </div>
              </div>

              <ModulesOverview
                visibleSectionIds={capabilities.visibleSectionIds}
                expandSectionIds={capabilities.expandSectionIds}
                isAdmin={effectiveIsAdmin}
                isLoading={isRolesLoading || companyRoles === null}
                boitageEnabled={boitageEnabled}
                restrictedToRowKeys={restrictedToRowKeys}
                hideHeader
                defaultAllCollapsed
              />

              {/* Section 3: Strategy + Planning */}
              <DashboardStrategySection
                sectionTitle="Le planning stratégique planifié sur 12 mois"
                showPlanningToggle
                isPlanningOpen={isPlanningOpen}
                onTogglePlanning={() => setIsPlanningOpen((v) => !v)}
              />

              {isPlanningOpen && capabilities.canSeePlanning && (
                <PlanningModule restrictedToRowKeys={restrictedToRowKeys} />
              )}
            </>
          ) : (
            <>
              {/* ═══ Focused Layout (transactionnaire / gestionnaire locatif / intervenant) ═══ */}

              {/* Section 1: Next actions */}
              {nextActions.length > 0 && (
                <NextActionsSection
                  actions={nextActions}
                  userName={userName}
                  companyUsers={companyUsers}
                />
              )}

              {/* Section 2: Role-specific modules (expanded) */}
              {focusedSectionLabel && (
                <div className="flex items-center justify-between rounded-[10px] bg-gradient-to-r from-rgpd-blue-dark to-rgpd-blue px-5 py-3.5 shadow-md">
                  <div className="flex items-center gap-3">
                    <div>
                      <div className="text-base font-extrabold text-white">
                        Les outils du module CROISSANCE : {focusedSectionLabel}
                      </div>
                      {focusedModuleCount > 0 && (
                        <div className="mt-0.5 text-xs font-normal text-white/75">
                          {focusedModuleCount} module{focusedModuleCount > 1 ? 's' : ''} dédié{focusedModuleCount > 1 ? 's' : ''} au {focusedSectionLabel.toLowerCase()}
                        </div>
                      )}
                    </div>
                  </div>
                  {userName && (
                    <span className="text-xs font-bold whitespace-nowrap text-white/90">
                      {userName}
                    </span>
                  )}
                </div>
              )}

              <ModulesOverview
                visibleSectionIds={capabilities.visibleSectionIds}
                expandSectionIds={capabilities.expandSectionIds}
                isAdmin={effectiveIsAdmin}
                isLoading={isRolesLoading || companyRoles === null}
                boitageEnabled={boitageEnabled}
                restrictedToRowKeys={restrictedToRowKeys}
                hideHeader
                flatMode
              />

              {/* Section 3: Strategy + Planning */}
              <DashboardStrategySection
                showPlanningToggle
                isPlanningOpen={isPlanningOpen}
                onTogglePlanning={() => setIsPlanningOpen((v) => !v)}
              />

              {isPlanningOpen && capabilities.canSeePlanning && (
                <PlanningModule restrictedToRowKeys={restrictedToRowKeys} />
              )}

            </>
          )}

          <SubscriptionBanner company={company} jwt={jwt} />
        </div>

        <StrategyModal
          isOpen={isStrategyModalOpen}
          onClose={() => setIsStrategyModalOpen(false)}
        />

        {isAdmin && (
          <RolesDemoPopup
            isOpen={isRolesPopupOpen}
            onClose={() => setIsRolesPopupOpen(false)}
            selectedRoles={demoRoles}
            onChange={setDemoRoles}
          />
        )}
      </div>
    </div>
  );
}

export default NewDashboard;
